import { assignIn } from 'lodash';
import { cloudInsightsColumns } from 'client/CloudInsights/constants/CloudInsightsConstants';
import CloudInsightsAccountsApi from 'client/api/v2/CloudInsightsAccountsApi';
import CollectionApi from 'client/api/v2/CollectionApi';
import Constants from 'client/util/Constants';
import axios from 'axios';
import moment from 'moment';

const { dataNotAvailable } = Constants;
const { jumpcloudUser, ssoUser } = cloudInsightsColumns;

const sanitizeEventId = (event) => {
  assignIn(event, {
    _id: event.event.eventId,
    ...event,
  });
};

const getSsoFilter = (value) => {
  if (value === dataNotAvailable) {
    return {
      termConjunction: {
        operator: 'and',
        termItems: [
          {
            termValue: {
              key: jumpcloudUser,
              value: 'false',
            },
          },
          {
            termValue: {
              key: ssoUser,
              value: 'false',
            },
          },
        ],
      },
    };
  }

  return {
    termValue: {
      key: value === 'SSO' ? 'ssoUser' : 'jumpcloudUser',
      value: 'true',
    },
  };
};

const getSearchTermParam = (searchTerm, filters) => {
  let searchTermParam = { singleTerm: searchTerm || '' };

  // Remove any filters that don't have values
  const trueFilters = filters.filter((f) => (f.values.length > 0));

  if (trueFilters.length) {
    const termItems = trueFilters.map((filter) => {
      if (filter.values.length > 1) {
        const termValues = filter.values.map((value) => {
          if (filter.field === 'connector') return getSsoFilter(value);
          return {
            termValue: {
              key: filter.field,
              value,
            },
          };
        });
        return {
          termConjunction: {
            operator: 'or',
            termItems: termValues,
          },
        };
      }
      if (filter.field === 'connector') return getSsoFilter(filter.values[0]);
      return {
        termValue: {
          key: filter.field,
          value: filter.values[0],
        },
      };
    });

    searchTermParam = {
      andConjunction: {
        operator: 'and',
        termItems,
      },
    };
  }

  return searchTermParam;
};

class CloudInsightsEventsApi extends CollectionApi {
  constructor() {
    const descendingEventTime = `-${cloudInsightsColumns.eventTime}`;

    super('/api/v2/cloudinsights/events', {
      skip: 0,
      limit: 100,
      sort: descendingEventTime,
      search: {
        searchTerm: '',
      },
    });
  }

  async deleteSavedView(id) {
    await this.initialize();
    const response = await axios.delete(`/api/v2/cloudinsights/views/${id}`);
    return response.data;
  }

  async fetch(
    skip,
    limit,
    sort,
    searchTerm,
    cancelToken,
    filters = [],
    nextPageToken,
    skipToken,
    dateTimeRange,
    nonPersonEntityFilter,
    accounts,
    searchAfterParam,
  ) {
    // const isAscending = !sort.includes('-');
    const startSeconds = moment(new Date(dateTimeRange.start)).unix();
    const endSeconds = moment(new Date(dateTimeRange.end)).unix();

    let fetchedAccounts = [];

    // If accounts has not been fetched, get accounts list
    if (!accounts.length) {
      const accountsResponse = await CloudInsightsAccountsApi.fetch(0, 100);
      fetchedAccounts = accountsResponse.results.map((account) => (account.awsAccountId));
    }

    const isAscending = !sort.startsWith('-');
    const field = isAscending ? sort : sort.slice(1);

    const searchTermParam = getSearchTermParam(searchTerm, filters);

    const payload = {
      awsAccounts: [...accounts, ...fetchedAccounts],
      startTime: { seconds: startSeconds },
      endTime: { seconds: endSeconds },
      npeFilter: nonPersonEntityFilter,
      limit,
      skip,
      sort: { field, isAscending },
      searchTerm: searchTermParam,
      searchAfter: searchAfterParam,
    };

    await this.initialize();
    const response = await axios.post(`${this.baseUrl}`, payload);

    response.data.events.forEach(sanitizeEventId);

    const { events, totalCount, searchAfter } = response.data;
    return { results: events, totalCount, searchAfter };
  }

  async fetchDistinct(
    field,
    dateTimeRange,
    accounts,
    nonPersonEntityFilter,
    searchTerm,
    filters = [],
  ) {
    const startSeconds = moment(new Date(dateTimeRange.start)).unix();
    const endSeconds = moment(new Date(dateTimeRange.end)).unix();

    let fetchedAccounts = [];

    // If accounts has not been fetched, get accounts list
    if (!accounts.length) {
      const accountsResponse = await CloudInsightsAccountsApi.fetch(0, 100);
      fetchedAccounts = accountsResponse.results.map((account) => (account.awsAccountId));
    }

    const payload = {
      field,
      awsAccounts: [...accounts, ...fetchedAccounts],
      startTime: { seconds: startSeconds },
      endTime: { seconds: endSeconds },
      npeFilter: nonPersonEntityFilter,
      searchTerm: getSearchTermParam(searchTerm, filters),
    };

    await this.initialize();
    const response = await axios.post(`${this.baseUrl}/distinct`, payload);
    return response.data.results;
  }

  async fetchSavedViews() {
    await this.initialize();
    const response = await axios.get('/api/v2/cloudinsights/views');
    return response.data;
  }

  async putView(savedView) {
    const payload = {
      columns: savedView.columns,
      filters: savedView.filters,
      excludeNonPersonEntities: savedView.nonPersonEntity,
      name: savedView.viewName,
    };
    await this.initialize();
    const response = await axios.put(`/api/v2/cloudinsights/views/${savedView.viewId}`, payload);
    return response.data;
  }

  async saveView(savedView) {
    const payload = {
      columns: savedView.columns,
      filters: savedView.filters,
      excludeNonPersonEntities: savedView.nonPersonEntity,
      name: savedView.viewName,
    };
    await this.initialize();
    const response = await axios.post('/api/v2/cloudinsights/views', payload);
    return response.data;
  }
}

export default new CloudInsightsEventsApi();
